import * as React from "react";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { HeaderActionsRow } from "../ContextActions";
import type { CollapsibleSectionProps } from "./CollapsibleSection.types";
import { SectionDragHandleIcon } from "./SectionDragHandleIcon";
import { useSortableSections } from "./SortableSectionsContext";
import * as s from "./CollapsibleSection.style.css";

export type { CollapsibleSectionProps, CollapsibleSectionHighlight } from "./CollapsibleSection.types";

function resolveHighlight(
  highlight: CollapsibleSectionProps["highlight"],
): "accent" | "warning" | "success" | "info" | false {
  if (!highlight) return false;
  if (highlight === true || highlight === "accent") return "accent";
  return highlight;
}

export function CollapsibleSection({
  id,
  title,
  subtitle,
  icon,
  expanded,
  onToggleExpanded,
  statusIndicators,
  contextActions,
  contextActionsDisplay = "buttons",
  highlight,
  mutedHeader = false,
  compactHeader = false,
  flushBody = false,
  unmountCollapsed = false,
  children,
  className,
}: CollapsibleSectionProps) {
  const bodyId = `${id}-body`;
  const highlightKind = resolveHighlight(highlight);
  const bodyChildren =
    unmountCollapsed && !expanded ? null : children;

  const sortable = useSortableSections();
  const dragIdx = sortable.enabled ? sortable.sectionIds.indexOf(id) : -1;
  const isDragSrc = sortable.dragSrcId === id;
  const isSrcHover = isDragSrc && sortable.dragSrcHover;
  const dropEdge =
    sortable.dropLine?.id === id && !isDragSrc ? sortable.dropLine.edge : null;
  const isEntering = sortable.enteringIds.has(id);
  const showHandle = sortable.enabled && dragIdx >= 0;
  const dragActive = sortable.dragSrcId != null;

  return (
    <div
      id={id}
      data-section-nav-id={id}
      data-section-drag-idx={dragIdx >= 0 ? dragIdx : undefined}
      className={clsx(
        s.sectionCard,
        highlightKind === "accent" && s.highlightAccent,
        highlightKind === "warning" && s.highlightWarning,
        highlightKind === "success" && s.highlightSuccess,
        highlightKind === "info" && s.highlightInfo,
        isDragSrc && s.sectionDragging,
        dropEdge === "before" && s.dropLineBefore,
        dropEdge === "after" && s.dropLineAfter,
        isEntering && s.sectionEnter,
        className,
      )}
    >
      {isDragSrc ? (
        <div
          className={clsx(
            s.sectionDraggingPlaceholder,
            isSrcHover && s.sectionDragSrcHover,
          )}
          aria-hidden
        />
      ) : null}
      <div className={clsx(isDragSrc && s.sectionDraggingHide)}>
        <div
          className={clsx(
            s.sectionHeader,
            mutedHeader && s.sectionHeaderMuted,
            compactHeader && s.sectionHeaderCompact,
            !expanded && s.sectionHeaderCollapsed,
            !expanded && compactHeader && s.sectionHeaderCollapsedCompact,
          )}
          style={{ padding: compactHeader ? "10px 20px" : "36px 40px" }}
        >
          <div className={s.titleRow}>
            <div
              className={clsx(
                s.titleCluster,
                compactHeader && s.titleClusterCompact,
              )}
            >
              {showHandle ? (
                <div
                  className={clsx(
                    s.titleHoverTarget,
                    compactHeader && s.titleHoverTargetCompact,
                    dragActive && s.titleHoverTargetInactive,
                  )}
                >
                  <button
                    type="button"
                    data-section-drag-handle=""
                    className={clsx(
                      s.dragHandle,
                      compactHeader && s.dragHandleCompact,
                      isDragSrc && s.dragHandleRevealed,
                    )}
                    aria-label={`Reorder ${title}`}
                    onPointerDown={(e) => sortable.onHandlePointerDown(id, title, e)}
                    onClick={(e) => e.preventDefault()}
                  >
                    <SectionDragHandleIcon box={compactHeader ? 16 : 20} />
                  </button>
                  <div
                    data-section-title-slide=""
                    className={clsx(
                      s.titleSlide,
                      compactHeader && s.titleSlideCompact,
                      isDragSrc &&
                        (compactHeader
                          ? s.titleSlideRevealedCompact
                          : s.titleSlideRevealed),
                    )}
                  >
                    <span
                      className={clsx(s.titleIcon, compactHeader && s.titleIconCompact)}
                      aria-hidden
                      style={{ alignSelf: "center" }}
                    >
                      {icon}
                    </span>
                    <h3
                      className={clsx(s.sectionTitle, compactHeader && s.sectionTitleCompact)}
                      style={
                        compactHeader
                          ? {
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: 1.2,
                              margin: 0,
                              padding: 0,
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                            }
                          : {
                              fontSize: 24,
                              fontWeight: 700,
                              lineHeight: 1.2,
                              margin: 0,
                              padding: 0,
                            }
                      }
                    >
                      {title}
                    </h3>
                  </div>
                </div>
              ) : (
                <>
                  <span
                    className={clsx(s.titleIcon, compactHeader && s.titleIconCompact)}
                    aria-hidden
                    style={{ alignSelf: "center" }}
                  >
                    {icon}
                  </span>
                  <h3
                    className={clsx(s.sectionTitle, compactHeader && s.sectionTitleCompact)}
                    style={
                      compactHeader
                        ? {
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1.2,
                            margin: 0,
                            padding: 0,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                          }
                        : {
                            fontSize: 24,
                            fontWeight: 700,
                            lineHeight: 1.2,
                            margin: 0,
                            padding: 0,
                          }
                    }
                  >
                    {title}
                  </h3>
                </>
              )}
              {subtitle ? (
                <span
                  data-section-title-subtitle={showHandle ? "" : undefined}
                  className={clsx(
                    s.sectionSubtitle,
                    compactHeader && s.sectionSubtitleCompact,
                    showHandle && s.sectionSubtitleWithHandle,
                    showHandle && compactHeader && s.sectionSubtitleWithHandleCompact,
                    showHandle &&
                      isDragSrc &&
                      (compactHeader
                        ? s.sectionSubtitleRevealedCompact
                        : s.sectionSubtitleRevealed),
                  )}
                  style={{
                    fontSize: compactHeader ? 11 : 13,
                    lineHeight: 1.2,
                    fontWeight: 500,
                  }}
                >
                  {subtitle}
                </span>
              ) : null}
            </div>
          </div>

          <HeaderActionsRow
            statusIndicators={statusIndicators}
            contextActions={contextActions}
            contextActionsDisplay={contextActionsDisplay}
            trailing={
              <button
                type="button"
                className={s.sectionToggle}
                onClick={onToggleExpanded}
                aria-expanded={expanded}
                aria-controls={bodyId}
                aria-label={expanded ? "Collapse section" : "Expand section"}
              >
                <ChevronDown
                  size={16}
                  className={clsx(s.sectionToggleIcon, !expanded && s.sectionToggleIconCollapsed)}
                  aria-hidden
                />
              </button>
            }
          />
        </div>

        <div
          id={bodyId}
          className={clsx(s.sectionBody, !expanded && s.sectionBodyCollapsed)}
          aria-hidden={!expanded}
        >
          <div
            className={clsx(
              s.sectionBodyInner,
              expanded && s.sectionBodyInnerExpanded,
            )}
          >
            <div
              className={clsx(
                s.sectionBodyContent,
                flushBody && s.sectionBodyContentFlush,
              )}
              style={flushBody ? { padding: 0 } : { padding: "6px 40px 36px" }}
            >
              {bodyChildren}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
